import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Flame,
  LineChart,
  Timer,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react'

const BORDER = '1px solid rgba(255,255,255,0.06)'
const GAP = 16

const URG_RANK = { high: 0, medium: 1, low: 2 }

/** @typedef {'dormance'|'surcote'|'concurrence'|'pression'|'souscote'|'marche'} CategoryKey */

/** @param {{ categoryKey?: string, type?: string }} a */
function resolveCategoryKey(a) {
  const k = a?.categoryKey
  if (
    k === 'dormance' ||
    k === 'surcote' ||
    k === 'concurrence' ||
    k === 'pression' ||
    k === 'souscote' ||
    k === 'marche'
  ) {
    return k
  }
  if (a?.type === 'competitor_alert') return 'concurrence'
  if (a?.type === 'opportunity') return 'souscote'
  return 'dormance'
}

/** @type {Record<CategoryKey, { pill: string, borderLeft: string, cardBg: string, iconWrap: string, iconColor: string, primaryBtnBg: string, primaryBtnBorder: string, primaryBtnColor: string }>} */
const CATEGORY_STYLE = {
  dormance: {
    pill: 'Rotation stock',
    borderLeft: '#A07530',
    cardBg: 'linear-gradient(135deg, rgba(224,184,120,0.09) 0%, rgba(255,255,255,0.02) 55%)',
    iconWrap: 'rgba(160,117,48,0.22)',
    iconColor: '#fbbf24',
    primaryBtnBg: 'rgba(160,117,48,0.2)',
    primaryBtnBorder: '1px solid rgba(224,184,120,0.45)',
    primaryBtnColor: '#fde68a',
  },
  surcote: {
    pill: 'Surcôte vs marché',
    borderLeft: '#dc2626',
    cardBg: 'linear-gradient(135deg, rgba(248,113,113,0.08) 0%, rgba(255,255,255,0.02) 55%)',
    iconWrap: 'rgba(220,38,38,0.2)',
    iconColor: '#fca5a5',
    primaryBtnBg: 'rgba(127,29,29,0.35)',
    primaryBtnBorder: '1px solid rgba(248,113,113,0.4)',
    primaryBtnColor: '#fecaca',
  },
  concurrence: {
    pill: 'Concurrence',
    borderLeft: '#1F4D82',
    cardBg: 'linear-gradient(135deg, rgba(0,30,80,0.2) 0%, rgba(255,255,255,0.02) 55%)',
    iconWrap: 'rgba(0,30,80,0.35)',
    iconColor: '#93c5fd',
    primaryBtnBg: 'rgba(0,30,80,0.35)',
    primaryBtnBorder: '1px solid rgba(96,165,250,0.45)',
    primaryBtnColor: '#dbeafe',
  },
  // FIX 2026-05 : nouvelle catégorie "Pression marché" pour les cas où les
  // concurrents directs sont nettement moins chers (critique) ou qu'une
  // surcôte est confirmée par ≥10 références de cotation. Couleur orange/
  // brûlé pour la distinguer de la surcôte simple (rouge) et de la
  // concurrence informative (bleu).
  pression: {
    pill: 'Pression marché',
    borderLeft: '#c2410c',
    cardBg: 'linear-gradient(135deg, rgba(234,88,12,0.12) 0%, rgba(255,255,255,0.02) 55%)',
    iconWrap: 'rgba(194,65,12,0.28)',
    iconColor: '#fdba74',
    primaryBtnBg: 'rgba(154,52,18,0.35)',
    primaryBtnBorder: '1px solid rgba(251,146,60,0.45)',
    primaryBtnColor: '#fed7aa',
  },
  souscote: {
    pill: 'Marge / reprise',
    borderLeft: '#15803d',
    cardBg: 'linear-gradient(135deg, rgba(74,222,128,0.08) 0%, rgba(255,255,255,0.02) 55%)',
    iconWrap: 'rgba(22,101,52,0.28)',
    iconColor: '#86efac',
    primaryBtnBg: 'rgba(22,101,52,0.35)',
    primaryBtnBorder: '1px solid rgba(74,222,128,0.4)',
    primaryBtnColor: '#bbf7d0',
  },
  marche: {
    pill: 'Marché',
    borderLeft: '#64748b',
    cardBg: 'linear-gradient(135deg, rgba(148,163,184,0.1) 0%, rgba(255,255,255,0.02) 55%)',
    iconWrap: 'rgba(100,116,139,0.25)',
    iconColor: '#cbd5e1',
    primaryBtnBg: 'rgba(30,41,59,0.55)',
    primaryBtnBorder: '1px solid rgba(148,163,184,0.35)',
    primaryBtnColor: '#e2e8f0',
  },
}

/** Style des chips secondaires (signaux complémentaires) — palette discrète. */
const SECONDARY_TAG_STYLE = {
  dormance: { color: '#fcd34d', border: 'rgba(252,211,77,0.45)', bg: 'rgba(120,53,15,0.18)' },
  surcote: { color: '#fca5a5', border: 'rgba(252,165,165,0.5)', bg: 'rgba(127,29,29,0.22)' },
  pression: { color: '#fdba74', border: 'rgba(253,186,116,0.5)', bg: 'rgba(154,52,18,0.22)' },
  concurrence: { color: '#93c5fd', border: 'rgba(147,197,253,0.5)', bg: 'rgba(30,58,138,0.22)' },
  souscote: { color: '#86efac', border: 'rgba(134,239,172,0.5)', bg: 'rgba(20,83,45,0.22)' },
}

function CategoryIcon({ categoryKey }) {
  const c = { size: 18, strokeWidth: 1.75, color: 'currentColor' }
  switch (categoryKey) {
    case 'dormance':
      return <Timer {...c} aria-hidden />
    case 'surcote':
      return <TrendingUp {...c} aria-hidden />
    case 'concurrence':
      return <Users {...c} aria-hidden />
    case 'pression':
      return <Flame {...c} aria-hidden />
    case 'souscote':
      return <TrendingDown {...c} aria-hidden />
    case 'marche':
      return <LineChart {...c} aria-hidden />
    default:
      return <AlertTriangle {...c} aria-hidden />
  }
}

function EmptyIllustration() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="24" cy="24" r="20" stroke="rgba(148,163,184,0.35)" strokeWidth="1.25" />
      <path
        d="M16 24.5 L21.5 30 L32 19"
        stroke="rgba(148,163,184,0.55)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * @typedef {'opportunity'|'risk'|'competitor_alert'} ActionType
 * @typedef {'high'|'medium'|'low'} UrgencyLevel
 * @typedef {{ label: string, onClick: () => void }} ActionCTA
 * @typedef {{ tone: 'dormance'|'surcote'|'pression'|'concurrence'|'souscote', label: string }} SecondaryTag
 * @typedef {{
 *   id: string,
 *   type: ActionType,
 *   categoryKey?: CategoryKey,
 *   title: string,
 *   subtitle: string,
 *   urgency: UrgencyLevel,
 *   secondaryTags?: SecondaryTag[],
 *   sortAt?: number,
 *   imageUrl?: string,
 *   primaryCTA: ActionCTA,
 *   secondaryCTA?: ActionCTA,
 *   expandedContent?: import('react').ReactNode
 * }} Action
 */

/** Stock / reprise / concurrence interne avant veille pure marché (listings externes). */
function stockBeforeMarche(a, b) {
  const ca = resolveCategoryKey(a)
  const cb = resolveCategoryKey(b)
  const ra = ca === 'marche' ? 1 : 0
  const rb = cb === 'marche' ? 1 : 0
  return ra - rb
}

/**
 * Tri de base (priorityRank → urgence → date), puis passe d'interleaving
 * pour éviter plus de MAX_STREAK cartes de la même catégorie à la suite.
 * Cela garantit de la variété visuelle même quand toutes les motos dorment.
 */
const MAX_STREAK = 2

function sortActions(actions) {
  const base = [...actions].sort((a, b) => {
    const pa = Number.isFinite(Number(a?.priorityRank)) ? Number(a.priorityRank) : 99
    const pb = Number.isFinite(Number(b?.priorityRank)) ? Number(b.priorityRank) : 99
    if (pa !== pb) return pa - pb
    const bucket = stockBeforeMarche(a, b)
    if (bucket !== 0) return bucket
    const ua = URG_RANK[a.urgency] ?? 9
    const ub = URG_RANK[b.urgency] ?? 9
    if (ua !== ub) return ua - ub
    return (Number(a.sortAt) || 0) - (Number(b.sortAt) || 0)
  })

  // Interleaving : on ne place jamais plus de MAX_STREAK items de la même
  // catégorie consécutivement. Quand la limite est atteinte, on pioche le
  // premier item disponible d'une autre catégorie (en respectant son rang).
  const result = []
  const remaining = [...base]
  let streak = 0
  let lastCat = null

  // FIX 2026-05 : on remplace `findIndex` (avec closure) par une boucle for
  // explicite pour eviter le warning eslint `no-loop-func` (les CI traitent
  // les warnings comme des erreurs).
  while (remaining.length > 0) {
    let idx = -1
    if (streak < MAX_STREAK) {
      idx = 0
    } else {
      for (let i = 0; i < remaining.length; i++) {
        if (resolveCategoryKey(remaining[i]) !== lastCat) {
          idx = i
          break
        }
      }
    }
    if (idx === -1) {
      // Toutes les restantes ont la meme categorie -> on vide tel quel.
      result.push(...remaining.splice(0))
      break
    }
    const [item] = remaining.splice(idx, 1)
    const cat = resolveCategoryKey(item)
    streak = cat === lastCat ? streak + 1 : 1
    lastCat = cat
    result.push(item)
  }

  return result
}

/**
 * File d’actions prioritaires (stock avant marché, tri urgence + date, expand, « voir tout »).
 * @param {{
 *   actions: Action[],
 *   title?: string,
 *   maxVisible?: number,
 *   idPrefix?: string,
 *   sectionId?: string,
 *   className?: string,
 *   listLabel?: string,
 * }} props
 */
export function ActionQueue({
  actions,
  title = 'À traiter maintenant',
  maxVisible = 8,
  idPrefix = 'aq',
  sectionId,
  className,
  listLabel = 'File d’actions prioritaires',
}) {
  const [expandedId, setExpandedId] = useState(null)
  const [showAll, setShowAll] = useState(false)
  const sorted = useMemo(() => sortActions(actions), [actions])
  const total = sorted.length
  const visible = showAll ? sorted : sorted.slice(0, maxVisible)
  const rowRefs = useRef([])

  useEffect(() => {
    rowRefs.current = rowRefs.current.slice(0, visible.length)
  }, [visible.length])

  const toggle = (id) => {
    setExpandedId((cur) => (cur === id ? null : id))
  }

  const focusRow = (idx) => {
    const el = rowRefs.current[idx]
    if (el && typeof el.focus === 'function') el.focus()
  }

  const onRowKeyDown = (e, idx, id, expandable) => {
    if (expandable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      toggle(id)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (idx < visible.length - 1) focusRow(idx + 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (idx > 0) focusRow(idx - 1)
    }
  }

  if (total === 0) {
    return (
      <section
        id={sectionId}
        className={['action-queue', className].filter(Boolean).join(' ')}
        aria-label={listLabel}
        style={{ scrollMarginTop: 12 }}
      >
        {title ? (
          <h2 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{title}</h2>
        ) : null}
        <div
          style={{
            border: BORDER,
            borderRadius: 6,
            background: 'rgba(255,255,255,0.03)',
            padding: '28px 20px',
            textAlign: 'center',
          }}
        >
          <EmptyIllustration />
          <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>Rien d&apos;urgent aujourd&apos;hui</p>
        </div>
      </section>
    )
  }

  return (
    <section
      id={sectionId}
      className={['action-queue', className].filter(Boolean).join(' ')}
      aria-label={listLabel}
      style={{ scrollMarginTop: 12 }}
    >
      {title ? (
        <h2 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{title}</h2>
      ) : null}
      <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
        {visible.map((a, idx) => {
          const open = expandedId === a.id
          const expandable = a.expandedContent != null
          const panelId = `${idPrefix}-panel-${a.id}`
          const rowId = `${idPrefix}-row-${a.id}`
          const cat = resolveCategoryKey(a)
          const st = CATEGORY_STYLE[cat]

          return (
            <article
              key={a.id}
              role="listitem"
              style={{
                border: BORDER,
                borderLeft: `3px solid ${st.borderLeft}`,
                borderRadius: 6,
                background: st.cardBg,
                padding: '12px 14px',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
              }}
            >
              <div
                ref={(el) => {
                  rowRefs.current[idx] = el
                }}
                id={rowId}
                role={expandable ? 'button' : 'group'}
                tabIndex={0}
                aria-expanded={expandable ? open : undefined}
                aria-controls={expandable ? panelId : undefined}
                className="action-queue-focus-row"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  cursor: expandable ? 'pointer' : 'default',
                  outline: 'none',
                }}
                onClick={() => expandable && toggle(a.id)}
                onKeyDown={(e) => onRowKeyDown(e, idx, a.id, expandable)}
              >
                <div
                  style={{
                    marginTop: 2,
                    width: 36,
                    height: 36,
                    borderRadius: 6,
                    background: st.iconWrap,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: st.iconColor,
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {a.imageUrl ? (
                    <img
                      src={a.imageUrl}
                      alt=""
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        const next = e.currentTarget.nextElementSibling
                        if (next) next.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  <span style={{ display: a.imageUrl ? 'none' : 'flex' }}>
                    <CategoryIcon categoryKey={cat} />
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: st.iconColor,
                        border: `1px solid ${st.borderLeft}`,
                        background: 'rgba(15,23,42,0.35)',
                        padding: '3px 8px',
                        borderRadius: 4,
                      }}
                    >
                      {st.pill}
                    </span>
                    {Array.isArray(a.secondaryTags) && a.secondaryTags.length
                      ? a.secondaryTags.slice(0, 3).map((tag, i) => {
                          const ts = SECONDARY_TAG_STYLE[tag?.tone] || SECONDARY_TAG_STYLE.concurrence
                          return (
                            <span
                              key={`${a.id}-tag-${i}`}
                              style={{
                                fontSize: 9,
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                                color: ts.color,
                                border: `1px solid ${ts.border}`,
                                background: ts.bg,
                                padding: '2px 7px',
                                borderRadius: 999,
                              }}
                            >
                              {tag.label}
                            </span>
                          )
                        })
                      : null}
                    {a.urgency === 'high' ? (
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#fca5a5' }}>Priorité haute</span>
                    ) : a.urgency === 'medium' ? (
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#fcd34d' }}>Priorité moyenne</span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', lineHeight: 1.35 }}>{a.title}</div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      lineHeight: 1.4,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {a.subtitle}
                  </div>
                </div>
                {expandable ? (
                  <span style={{ color: 'var(--text-tertiary)', display: 'flex', padding: 4, flexShrink: 0 }} aria-hidden>
                    {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </span>
                ) : null}
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    a.primaryCTA.onClick()
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 4,
                    border: st.primaryBtnBorder,
                    background: st.primaryBtnBg,
                    color: st.primaryBtnColor,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {a.primaryCTA.label}
                </button>
                {a.secondaryCTA ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      a.secondaryCTA.onClick()
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 4,
                      border: BORDER,
                      background: 'transparent',
                      color: 'var(--text-secondary)',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {a.secondaryCTA.label}
                  </button>
                ) : null}
              </div>
              {expandable ? (
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={rowId}
                  aria-hidden={!open}
                  style={{
                    maxHeight: open ? 480 : 0,
                    opacity: open ? 1 : 0,
                    overflow: 'hidden',
                    transition: 'max-height 150ms ease, opacity 150ms ease',
                    marginTop: open ? 10 : 0,
                    paddingTop: open ? 10 : 0,
                    borderTop: open ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  }}
                >
                  <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{a.expandedContent}</div>
                </div>
              ) : null}
            </article>
          )
        })}
      </div>
      {total > maxVisible ? (
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll}
            aria-controls={sectionId ? `${sectionId}` : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              border: BORDER,
              background: 'rgba(255,255,255,0.04)',
              color: '#e2e8f0',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '8px 16px',
              borderRadius: 6,
              fontFamily: 'inherit',
              transition: 'background 120ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.07)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
            }}
          >
            {showAll ? (
              <>
                <ChevronDown size={16} style={{ transform: 'rotate(180deg)' }} aria-hidden />
                Voir moins
              </>
            ) : (
              <>
                <ChevronDown size={16} aria-hidden />
                Afficher {total - maxVisible} action{total - maxVisible > 1 ? 's' : ''} de plus
              </>
            )}
          </button>
        </div>
      ) : null}
      <style>{`
        .action-queue-focus-row:focus-visible {
          box-shadow: 0 0 0 2px rgba(147, 197, 253, 0.55);
          border-radius: 4px;
        }
        .action-queue article button:focus-visible {
          box-shadow: 0 0 0 2px rgba(147, 197, 253, 0.55);
          outline: none;
        }
      `}</style>
    </section>
  )
}
